import { useId } from 'react'
import { useDropdown } from '../../../shared/utils/DropdownContext'

type Option = { title: string; value: string }

interface CategorySelectProps {
  label?: string
  options: Option[]
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  className?: string
}

/**
 * Category picker for the product add/edit forms.
 *
 * Selection only — creating a category from here was removed for PE-2273 bug 4. Categories
 * are tenant-scoped platform taxonomy, not per-product data, and are managed on the Product
 * Metadata page's Categories tab. Letting the product form mint one inline produced ad-hoc
 * duplicates that no admin had approved.
 */
export const CategorySelect = ({ label, options, value, onChange, placeholder = 'Select category', className = '' }: CategorySelectProps) => {
  const dropdownId = useId()
  const { openDropdown, setOpenDropdown } = useDropdown()
  const showDrop = openDropdown === dropdownId

  const selectedOption = options.find(o => o.value === value)
  const displayText = selectedOption?.title || placeholder

  const handleSelect = (option: Option) => {
    onChange?.(option.value)
    setOpenDropdown(null)
  }

  return (
    <div>
      {label && <label className="block text-[14px] font-[600] text-foreground mb-1">{label}</label>}
      <div className={`relative ${className}`}>
        <div
          className="h-[38px] rounded-[10px] flex items-center justify-between p-2 cursor-pointer bg-muted hover:bg-muted border-[1px] border-[#D9D9D9]"
          onClick={() => setOpenDropdown(showDrop ? null : dropdownId)}
        >
          <span className="text-foreground text-[14px]">{displayText}</span>
          <svg width="12" height="7" viewBox="0 0 12 7" fill="none" className={`transform transition-transform ${showDrop ? 'rotate-180' : ''}`}>
            <path d="M10.1693 1.3335L6.0026 5.50016L1.83594 1.3335" stroke="#3F3E42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {showDrop && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-card rounded-[10px] p-3 z-10" style={{ boxShadow: '0px 13px 15px 5px #0302084D' }}>
            {options.length === 0 && (
              <p className="px-2 py-2 text-[14px] text-muted-foreground">
                No categories yet — add them on the Product Metadata page.
              </p>
            )}
            {options.map(option => (
              <div
                key={option.value}
                className="flex justify-between items-center p-2 hover:bg-muted rounded cursor-pointer"
                onClick={() => handleSelect(option)}
              >
                <span className="text-foreground font-[400] text-[16px]">{option.title}</span>
                {value === option.value && (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.3307 4L5.9974 11.3333L2.66406 8" stroke="#3F3E42" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            ))}

          </div>
        )}
      </div>
    </div>
  )
}
